/**
 * Builder CDN image transforms (`?width=` / `?format=`).
 *
 * Raw `cdn.builder.io/api/v1/image/assets/...` URLs serve the original
 * upload — homepage samples hit 1.3 MB PNGs in a 400 px slot. The CDN
 * honours `width` + `format=webp` and is a no-op for non-Builder URLs
 * (SFCC DIS, relative paths, tracking pixels).
 */

const BUILDER_IMAGE_PATH = /cdn\.builder\.io\/api\/v1\/image\//i;
const BUILDER_PIXEL_PATH = /cdn\.builder\.io\/api\/v1\/pixel/i;

export function isBuilderImageUrl(src: string | undefined): boolean {
  return Boolean(
    src && BUILDER_IMAGE_PATH.test(src) && !BUILDER_PIXEL_PATH.test(src),
  );
}

export function builderImageUrl(
  src: string | undefined,
  opts?: { width?: number; format?: "webp" | "jpg" | "png" },
): string {
  if (!src) return "";
  if (!isBuilderImageUrl(src)) return src;
  const qIndex = src.indexOf("?");
  const base = qIndex === -1 ? src : src.slice(0, qIndex);
  const params = new URLSearchParams(
    qIndex === -1 ? "" : src.slice(qIndex + 1),
  );
  // Builder Image's webp `<source>` does `url.replace(/\?/g, "?format=webp&")`,
  // which can leave `format=` twice. Collapse before we add anything.
  collapseDuplicateParams(params, "format");
  collapseDuplicateParams(params, "width");
  if (opts?.width && opts.width > 0 && !params.has("width")) {
    params.set("width", String(Math.round(opts.width)));
  }
  if (opts?.format && !params.has("format")) {
    params.set("format", opts.format);
  }
  const next = params.toString();
  return next ? `${base}?${next}` : base;
}

function collapseDuplicateParams(params: URLSearchParams, key: string): void {
  const values = params.getAll(key);
  if (values.length <= 1) return;
  const last = values[values.length - 1];
  params.delete(key);
  if (last) params.set(key, last);
}

export function builderImageSrcSet(
  src: string | undefined,
  widths: readonly number[],
): string | undefined {
  if (!src || !isBuilderImageUrl(src) || widths.length === 0) return undefined;
  const unique = [...new Set(widths.filter(w => w > 0))].sort((a, b) => a - b);
  if (unique.length === 0) return undefined;
  return unique
    .map(w => `${builderImageUrl(src, { width: w, format: "webp" })} ${w}w`)
    .join(", ");
}

/** 1x + 2x candidates capped at `maxCssWidth * 2`, plus a 400 floor for small slots. */
export function builderImageWidths(maxCssWidth: number): number[] {
  const cap = Math.max(64, Math.round(maxCssWidth * 2));
  const candidates = [400, 800, 1200, 1600].filter(w => w <= cap);
  if (!candidates.includes(cap)) candidates.push(cap);
  return candidates.sort((a, b) => a - b);
}
